import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Editor, Node, Transforms, Range, createEditor } from 'slate';
import { ReactEditor, Editable, Slate, withReact } from 'slate-react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import config from '@plone/volto/registry';
import { P } from '@plone/volto-slate/constants';
import { TitleSchema } from './schema';
import { SidebarPortal, BlockDataForm } from '@plone/volto/components';

const messages = defineMessages({
  title: {
    id: 'Type the title…',
    defaultMessage: 'Type the title…',
  },
});

function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export const TitleBlockEdit = (props) => {
  const {
    block,
    blockNode,
    data,
    detached,
    editable,
    index,
    metadata,
    onAddBlock,
    onChangeField,
    onFocusNextBlock,
    onFocusPreviousBlock,
    onSelectBlock,
    properties,
    selected,
  } = props;
  const intl = useIntl();

  const schema = TitleSchema(intl);
  const [editor] = useState(withReact(createEditor()));
  const [initialValue] = useState([
    {
      type: P,
      children: [{ text: metadata?.['title'] || properties?.['title'] || '' }],
    },
  ]);

  const prePopulateFields = (schema) => {
    const initialValues = {};
    Object.keys(schema.properties).forEach((key) => {
      if (schema.properties[key].hasOwnProperty('initialValue')) {
        initialValues[key] = schema.properties[key].initialValue;
      }
    });
    props.onChangeBlock(block, {
      ...initialValues,
      ...data,
    });
  };

  React.useEffect(() => {
    prePopulateFields(schema);
  }, []);


  const handleSidebarChange = (id, value) => {
    props.onChangeBlock(block, {
      ...data,
      [id]: value,
    });
  };

  const prevSelected = usePrevious(selected);

  const text = useMemo(
    () => metadata?.['title'] || properties?.['title'] || '',
    [metadata, properties],
  );

  const placeholder = useMemo(
    () => data.placeholder || intl.formatMessage(messages['title']),
    [data.placeholder, intl],
  );
  const disableNewBlocks = useMemo(() => detached, [detached]);

  useEffect(() => {
    if (!prevSelected && selected) {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        // keep selection
        ReactEditor.focus(editor);
      } else {
        // nothing is selected, move focus to end
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.end(editor, []));
      }
    }
  }, [prevSelected, selected, editor]);

  useEffect(() => {
    // undo/redo handlerr
    const oldText = Node.string(editor);
    if (oldText !== text) {
      Transforms.insertText(editor, text, {
        at: [0, 0],
      });
    }
  }, [editor, text]);

  const handleChange = useCallback(() => {
    const newText = Node.string(editor);
    if (newText !== text) {
      onChangeField('title', newText);
    }
  }, [editor, onChangeField, text]);

  const handleKeyDown = useCallback(
    (ev) => {
      if (ev.key === 'Return' || ev.key === 'Enter') {
        ev.preventDefault();
        if (!disableNewBlocks) {
          onSelectBlock(
            onAddBlock(config.settings.defaultBlockType, index + 1),
          );
        }
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        onFocusPreviousBlock(block, blockNode.current);
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        onFocusNextBlock(block, blockNode.current);
      }
    },
    [
      index,
      blockNode,
      disableNewBlocks,
      onSelectBlock,
      onAddBlock,
      onFocusPreviousBlock,
      onFocusNextBlock,
      block,
    ],
  );

  const handleFocus = useCallback(() => {
    onSelectBlock(block);
  }, [block, onSelectBlock]);

  const renderElement = useCallback(({ attributes, children }) => {
    return (
      <h1 {...attributes} className="documentFirstHeading">
        {children}
      </h1>
    );
  }, []);

  if (typeof window.__SERVER__ !== 'undefined') {
    return <div />;
  }

  const leftDesignElem = (
    <div className='leftDesign'>
      <svg width="620" xmlns="http://www.w3.org/2000/svg" height="15" fill="none">
        <path d="M0 7.5H600" className="fills" />
        <g className="strokes">
          <path
            d="M0 7.5H600"
            style={{
              fill: 'none',
              strokeWidth: 5,
              stroke: 'rgb(255, 255, 255)',
              strokeOpacity: 1,
              strokeDasharray: '0, 10',
              strokeLinecap: 'round',
            }}
            className="stroke-shape"
          />
        </g>
        <path
          d="M607.071 0c4.14 0 7.5 3.361 7.5 7.5 0 4.139-3.36 7.5-7.5 7.5a7.504 7.504 0 0 1-7.5-7.5c0-4.139 3.361-7.5 7.5-7.5Zm0 3a4.502 4.502 0 0 0-4.5 4.5c0 2.484 2.017 4.5 4.5 4.5 2.484 0 4.5-2.016 4.5-4.5s-2.016-4.5-4.5-4.5Z"
          style={{
            fill: 'rgb(255, 255, 255)',
            fillOpacity: 1,
          }}
          className="fills"
        />
        <g className="strokes">
          <path
            d="M607.071 0c4.14 0 7.5 3.361 7.5 7.5 0 4.139-3.36 7.5-7.5 7.5a7.504 7.504 0 0 1-7.5-7.5c0-4.139 3.361-7.5 7.5-7.5Zm0 3a4.502 4.502 0 0 0-4.5 4.5c0 2.484 2.017 4.5 4.5 4.5 2.484 0 4.5-2.016 4.5-4.5s-2.016-4.5-4.5-4.5Z"
            style={{
              fill: 'none',
              strokeWidth: 1,
              stroke: 'rgb(255, 255, 255)',
              strokeOpacity: 1,
            }}
            className="stroke-shape"
          />
        </g>
      </svg>
    </div>
  );

  const rightDesignElem = (
    <div className='rightDesign'>
      <svg width="1000" xmlns="http://www.w3.org/2000/svg" height="15" fill="none">
        <path d="M21.5 7.432h978.5" className="fills" />
        <g className="strokes">
          <path d="M21.5 7.432h978.5" style={{ fill: 'none', strokeWidth: 5, stroke: 'rgb(255, 255, 255)', strokeOpacity: 1, strokeDasharray: '0, 10', strokeLinecap: 'round' }} className="stroke-shape" />
        </g>
        <path d="M7.5 0C11.639 0 15 3.361 15 7.5c0 4.139-3.361 7.5-7.5 7.5A7.504 7.504 0 0 1 0 7.5C0 3.361 3.361 0 7.5 0Zm0 3A4.502 4.502 0 0 0 3 7.5C3 9.984 5.016 12 7.5 12S12 9.984 12 7.5 9.984 3 7.5 3Z" style={{ fill: 'rgb(255, 255, 255)', fillOpacity: 1 }} className="fills" />
        <g className="strokes">
          <path d="M7.5 0C11.639 0 15 3.361 15 7.5c0 4.139-3.361 7.5-7.5 7.5A7.504 7.504 0 0 1 0 7.5C0 3.361 3.361 0 7.5 0Zm0 3A4.502 4.502 0 0 0 3 7.5C3 9.984 5.016 12 7.5 12S12 9.984 12 7.5 9.984 3 7.5 3Z" style={{ fill: 'none', strokeWidth: 1, stroke: 'rgb(255, 255, 255)', strokeOpacity: 1 }} className="stroke-shape" />
        </g>
      </svg>
    </div>
  )

  return (
    <>

        <div className="full-width highlight-title">
          <div className="ui layout container">
            <Slate editor={editor} onChange={handleChange} initialValue={initialValue}>
              <Editable
                readOnly={!editable}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                renderElement={renderElement}
                onFocus={handleFocus}
                aria-multiline="false"
              ></Editable>
            </Slate>
          </div>
        {data.showLeftDesignElem && leftDesignElem}
        {data.showRightDesignElem && rightDesignElem}
        </div>

      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          formData={data}
          onChangeField={handleSidebarChange}
        />
      </SidebarPortal>
    </>
  );
};

TitleBlockEdit.propTypes = {
  properties: PropTypes.objectOf(PropTypes.any).isRequired,
  selected: PropTypes.bool.isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onChangeField: PropTypes.func.isRequired,
  onSelectBlock: PropTypes.func.isRequired,
  onDeleteBlock: PropTypes.func.isRequired,
  onAddBlock: PropTypes.func.isRequired,
  onFocusPreviousBlock: PropTypes.func.isRequired,
  onFocusNextBlock: PropTypes.func.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  editable: PropTypes.bool,
  detached: PropTypes.bool,
  blockNode: PropTypes.any,
};

TitleBlockEdit.defaultProps = {
  detached: false,
  editable: true,
};

export default TitleBlockEdit;
